import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { IonButton, IonContent, IonSpinner } from '@ionic/angular/standalone';
import {
  ConfirmarSenhaModalComponent,
  SenhaEtapa,
} from 'src/app/components/confirmar-senha/confirmar-senha-modal.component';
import { NavbarComponent } from 'src/app/components/navbar/navbar.component';
import { apiResult } from 'src/app/models/api-result.interface';
import { ApiService } from 'src/app/services/api/api.service';
import { ModalsService } from 'src/app/services/modals/modals.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { SubscriptionDetailsDTO } from 'src/app/models/subscription-dtos';
import { labelPeriodicidade } from 'src/app/utils/planos-grade';
import { permiteSimulacaoPagamento } from 'src/app/utils/homolog-env';

@Component({
  selector: 'app-minhas-assinaturas-plano',
  templateUrl: './minhas-assinaturas-plano.page.html',
  styleUrls: ['./minhas-assinaturas-plano.page.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IonContent, IonButton, IonSpinner, NavbarComponent, ConfirmarSenhaModalComponent],
})
export class MinhasAssinaturasPlanoPage {
  planosDisponiveis: Array<{
    id: number;
    titulo: string;
    valor: string;
    tipo?: string;
    periodicidade: string;
    descricao?: string;
    codigo?: string;
    tipo_mudanca?: string;
    label_quando?: string;
    valor_diferenca?: string;
    efetivar_em?: string;
    mensagem?: string;
  }> = [];
  trocaDisponivel = false;
  mensagemTroca = '';
  readonly permiteSimularUpgrade = permiteSimulacaoPagamento();
  mudancaAtiva: {
    id: number;
    tipo_mudanca: 'upgrade' | 'downgrade' | 'troca_ciclo' | 'cancelamento' | 'reativacao';
    status: string;
    valor_diferenca: string;
    metodo_pagamento?: string;
    efetivar_em?: string;
  } | null = null;
  previewTroca: {
    tipo: string;
    codigo: string;
    plano_destino_id: number;
    valor_diferenca: string;
    dias_restantes: number;
    mensagem: string;
    label_quando?: string;
    impacto_familia?: {
      destino_individual?: boolean;
      perde_acesso_em?: string | null;
      membros?: Array<{ nome?: string; perde_acesso_em?: string }>;
    };
  } | null = null;
  pagamentoUpgrade: {
    metodo_pagamento?: string;
    pix_copia_cola?: string;
    boleto_linha_digitavel?: string;
    boleto_url?: string;
    valor?: string;
  } | null = null;
  carregandoTroca = false;
  processandoTroca = false;
  processandoReativacao = false;
  carregando = true;
  erro = false;
  cancelando = false;
  /** Modal de senha por pares (igual ao login) após confirmar cancelamento. */
  senhaCancelModalAberto = false;
  isSubmittingSenhaCancel = false;
  alterandoMetodo = false;
  possuiAssinatura = false;
  proximaCobrancaData = '';
  plano = {
    titulo: '',
    valor: '',
    periodicidade: '',
    status: '',
    validadeInicio: '',
    validadeFim: '',
    metodoPagamento: '',
    cartaoMascarado: '',
    acessoAte: '',
  };
  acessoPosCancelamento = {
    temAcesso: false,
    motivo: '',
    acessoAte: '',
  };

  constructor(
    private readonly api: ApiService,
    private readonly modals: ModalsService,
    private readonly utils: UtilsService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  async ionViewWillEnter(): Promise<void> {
    await this.carregarDados();
  }

  async tentarNovamente(): Promise<void> {
    await this.carregarDados();
  }

  async simularTrocaPlano(planoDestinoId: number): Promise<void> {
    if (!this.possuiAssinatura || this.carregandoTroca || this.processandoTroca) return;
    this.carregandoTroca = true;
    this.previewTroca = null;
    this.cdr.markForCheck();
    try {
      const r = await this.api.post('assinaturasplano', {
        acao: 'preview_troca',
        plano_destino_id: planoDestinoId,
      });
      const data = (r.data as Record<string, unknown>) ?? {};
      this.previewTroca = {
        tipo: String(data['tipo_mudanca'] ?? data['tipo'] ?? 'downgrade'),
        codigo: String(data['codigo'] ?? ''),
        plano_destino_id: Number(data['plano_destino_id'] ?? planoDestinoId),
        valor_diferenca: String(data['valor_diferenca'] ?? '0.00'),
        dias_restantes: Number(data['dias_restantes'] ?? 0),
        mensagem: String(data['mensagem'] ?? ''),
        label_quando: String(data['label_quando'] ?? ''),
        impacto_familia: (data['impacto_familia'] as {
          destino_individual?: boolean;
          perde_acesso_em?: string | null;
          membros?: Array<{ nome?: string; perde_acesso_em?: string }>;
        }) ?? undefined,
      };
    } catch (error: unknown) {
      await this.modals.modalAlerta(
        'Não foi possível simular a troca',
        this.resolveApiErrorMessage(error) ?? 'Tente novamente em alguns instantes.'
      );
    } finally {
      this.carregandoTroca = false;
      this.cdr.markForCheck();
    }
  }

  async solicitarTrocaPlano(planoDestinoId: number): Promise<void> {
    if (!this.possuiAssinatura || this.processandoTroca) return;

    this.processandoTroca = true;
    this.cdr.markForCheck();
    try {
      const r = await this.api.post('assinaturasplano', {
        acao: 'solicitar_troca',
        plano_destino_id: planoDestinoId,
      });
      const data = (r.data as Record<string, unknown>) ?? {};
      const status = String(data['status'] ?? 'agendada');
      const pagamento = (data['pagamento'] as Record<string, unknown> | null) ?? null;
      this.pagamentoUpgrade = pagamento
        ? {
            metodo_pagamento: String(pagamento['metodo_pagamento'] ?? ''),
            pix_copia_cola: String(pagamento['pix_copia_cola'] ?? ''),
            boleto_linha_digitavel: String(pagamento['boleto_linha_digitavel'] ?? ''),
            boleto_url: String(pagamento['boleto_url'] ?? ''),
            valor: String(pagamento['valor'] ?? data['valor_diferenca'] ?? ''),
          }
        : null;

      const mensagem =
        status === 'pendente_pagamento'
          ? 'Solicitação criada. Pague a diferença com o mesmo meio da assinatura para liberar o upgrade agora.'
          : 'Solicitação criada. A troca será aplicada no próximo ciclo.';

      await this.utils.exibirToast(mensagem, 'success');
      this.previewTroca = null;
      await this.carregarDados();
    } catch (error: unknown) {
      await this.modals.modalAlerta(
        'Não foi possível solicitar a troca',
        this.resolveApiErrorMessage(error) ?? 'Tente novamente em alguns instantes.'
      );
    } finally {
      this.processandoTroca = false;
      this.cdr.markForCheck();
    }
  }

  async confirmarPagamentoUpgrade(): Promise<void> {
    if (!this.mudancaAtiva || this.mudancaAtiva.tipo_mudanca !== 'upgrade' || this.mudancaAtiva.status !== 'pendente_pagamento') {
      return;
    }
    if (!this.permiteSimularUpgrade) {
      await this.modals.modalAlerta(
        'Aguardando pagamento',
        'O upgrade será liberado automaticamente após a confirmação do pagamento da diferença.'
      );
      return;
    }

    this.processandoTroca = true;
    this.cdr.markForCheck();
    try {
      await this.api.put('assinaturasplano', {
        acao: 'confirmar_upgrade',
        mudanca_id: this.mudancaAtiva.id,
      });
      await this.utils.exibirToast('Upgrade aplicado com sucesso.', 'success');
      await this.carregarDados();
    } catch (error: unknown) {
      await this.modals.modalAlerta(
        'Não foi possível confirmar o upgrade',
        this.resolveApiErrorMessage(error) ?? 'Tente novamente em alguns instantes.'
      );
    } finally {
      this.processandoTroca = false;
      this.cdr.markForCheck();
    }
  }

  async reativarAssinatura(): Promise<void> {
    if (this.processandoReativacao) return;

    const modal = await this.modals.modalAlerta(
      'Reativar assinatura',
      'Deseja reativar sua assinatura e manter a renovação automática?',
      'Reativar',
      'Voltar'
    );
    const detalhe = await modal.onWillDismiss();
    if (!detalhe.data) return;

    this.processandoReativacao = true;
    this.cdr.markForCheck();
    try {
      await this.api.put('assinaturasplano', { acao: 'reativar_cancelamento' });
      await this.utils.exibirToast('Assinatura reativada com sucesso.', 'success');
      await this.carregarDados();
    } catch (error: unknown) {
      await this.modals.modalAlerta(
        'Não foi possível reativar',
        this.resolveApiErrorMessage(error) ?? 'Tente novamente em alguns instantes.'
      );
    } finally {
      this.processandoReativacao = false;
      this.cdr.markForCheck();
    }
  }

  async cancelarAssinatura(): Promise<void> {
    if (!this.possuiAssinatura || this.cancelando) return;
    const modal = await this.modals.modalAlerta(
      'Cancelar assinatura',
      'Tem certeza que deseja cancelar sua assinatura? O acesso aos benefícios será encerrado.',
      'Sim, cancelar',
      'Voltar'
    );
    const detalhe = await modal.onWillDismiss();
    if (!detalhe.data) return;
    this.senhaCancelModalAberto = true;
    this.cdr.markForCheck();
  }

  fecharModalSenhaCancel(): void {
    this.senhaCancelModalAberto = false;
    this.isSubmittingSenhaCancel = false;
    this.cdr.markForCheck();
  }

  async onSenhaCancelConfirmada(etapas: SenhaEtapa[]): Promise<void> {
    if (this.isSubmittingSenhaCancel || etapas.length !== 6) return;
    this.isSubmittingSenhaCancel = true;
    this.cdr.markForCheck();

    const senhaAtual = JSON.stringify(etapas);
    this.cancelando = true;
    try {
      await this.api.delete('assinaturasfamilia', { tipo: 'assinatura', senha_atual: senhaAtual });
      this.senhaCancelModalAberto = false;
      await this.utils.exibirToast('Assinatura cancelada com sucesso.', 'success');
      await this.carregarDados();
    } catch (error: unknown) {
      await this.modals.modalAlerta(
        'Não foi possível cancelar',
        this.resolveApiErrorMessage(error) ?? 'Tente novamente em alguns instantes.'
      );
    } finally {
      this.cancelando = false;
      this.isSubmittingSenhaCancel = false;
      this.cdr.markForCheck();
    }
  }

  async alterarMetodoPagamento(metodo: 'cartao' | 'boleto' | 'pix'): Promise<void> {
    if (!this.possuiAssinatura || this.alterandoMetodo || this.plano.metodoPagamento === metodo) return;
    if (metodo === 'cartao') {
      this.utils.navigateForward('/minhas-assinaturas/plano/cartao');
      return;
    }

    const metodoAtual = this.plano.metodoPagamento;
    const trocaEntrePixEBoleto =
      (metodoAtual === 'pix' && metodo === 'boleto') ||
      (metodoAtual === 'boleto' && metodo === 'pix');

    if (trocaEntrePixEBoleto) {
      const dias = this.calcularDiasAteProximaCobranca();
      const emDias = dias === null
        ? 'em breve'
        : `em ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
      const novoMetodoLabel = this.labelMetodoPagamento(metodo);

      const modal = await this.modals.modalAlerta(
        'Alterar forma de pagamento',
        `A próxima cobrança, que vence ${emDias}, poderá ser paga utilizando ${novoMetodoLabel} assim que o pagamento da mensalidade estiver disponível.`,
        'Entendi, continuar',
        'Voltar'
      );
      const detalhe = await modal.onWillDismiss();
      if (!detalhe.data) return;
    }

    this.alterandoMetodo = true;
    try {
      const gatewayToken = '';
      const cartaoMascarado = '';
      const res = await this.api.put('assinaturaspagamento', {
        metodo_pagamento: metodo,
        gateway_payment_token: gatewayToken,
        cartao_mascarado: cartaoMascarado,
      });
      const msgApi = typeof res?.message === 'string' && res.message.trim() !== ''
        ? res.message
        : 'Forma de pagamento atualizada. A próxima cobrança será na janela de renovação (D-7).';
      await this.utils.exibirToast(msgApi, 'success');
      await this.carregarDados();
    } catch (error: unknown) {
      await this.modals.modalAlerta(
        'Não foi possível alterar o pagamento',
        this.resolveApiErrorMessage(error) ?? 'Tente novamente em alguns instantes.'
      );
    } finally {
      this.alterandoMetodo = false;
      this.cdr.markForCheck();
    }
  }

  formatarValor(valor: string): string {
    const n = Number(valor);
    if (!Number.isFinite(n)) return valor || '-';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatarData(dataIso: string): string {
    if (!dataIso) return '-';
    const [datePart] = dataIso.split(' ');
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return dataIso;
    return `${d}/${m}/${y}`;
  }

  rotuloPeriodo(periodicidade: string): string {
    return labelPeriodicidade(periodicidade);
  }

  rotuloAcaoTroca(item: { codigo?: string; label_quando?: string }): string {
    if (item.codigo === 'U_agora') return 'Trocar agora';
    return item.label_quando ? `Agendar ${item.label_quando}` : 'Agendar troca';
  }

  previewNomesFamilia(membros?: Array<{ nome?: string }> | null): string {
    if (!membros?.length) return '';
    return membros.map((membro) => membro.nome || 'membro').join(', ');
  }

  private async carregarDados(): Promise<void> {
    this.carregando = true;
    this.erro = false;
    this.possuiAssinatura = false;
    this.planosDisponiveis = [];
    this.mudancaAtiva = null;
    try {
      const familia: apiResult = await this.api.get('assinaturasfamilia');
      const data = (familia.data as Record<string, unknown>) ?? {};
      this.possuiAssinatura = Boolean(data['possui_assinatura']);
      const details = ((data['subscription_details'] as SubscriptionDetailsDTO) ?? null);
      const nextCharge = details?.next_charge ?? null;
      const gateway = details?.gateway ?? null;

      const assinatura = (data['assinatura'] as Record<string, unknown>) ?? {};
      const plano = (data['plano'] as Record<string, unknown>) ?? {};
      const paymentMethod = (details?.payment_method ?? null) as { metodo?: string; cardMasked?: string } | null;
      this.plano = {
        titulo: String(plano['titulo'] ?? ''),
        valor: String(plano['valor'] ?? ''),
        periodicidade: String(plano['periodicidade'] ?? 'mensal'),
        status: String(assinatura['status'] ?? ''),
        validadeInicio: String(assinatura['validade_inicio'] ?? ''),
        validadeFim: String(assinatura['validade_fim'] ?? ''),
        metodoPagamento: String(data['metodo_pagamento'] ?? paymentMethod?.metodo ?? ''),
        cartaoMascarado: String(assinatura['cartao_mascarado'] ?? paymentMethod?.cardMasked ?? ''),
        acessoAte: String(details?.access?.access_until ?? assinatura['validade_fim'] ?? ''),
      };

      this.proximaCobrancaData =
        String(
          nextCharge?.data ??
          nextCharge?.boleto?.vencimento ??
          gateway?.next_charge_at ??
          assinatura['validade_fim'] ??
          ''
        ) || '';

      this.acessoPosCancelamento = {
        temAcesso: Boolean(details?.access?.has_access),
        motivo: String(details?.access?.reason ?? ''),
        acessoAte: String(details?.access?.access_until ?? ''),
      };

      await this.carregarDadosTrocaPlano();
    } catch {
      this.erro = true;
    } finally {
      this.carregando = false;
      this.cdr.markForCheck();
    }
  }

  private resolveApiErrorMessage(error: unknown): string | null {
    if (error && typeof error === 'object' && error !== null) {
      const msg = (error as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim() !== '') return msg;
    }
    return null;
  }

  private calcularDiasAteProximaCobranca(): number | null {
    const data = this.normalizarData(this.proximaCobrancaData);
    if (!data) return null;

    const hoje = new Date();
    const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const inicioCobranca = new Date(data.getFullYear(), data.getMonth(), data.getDate());
    const ms = inicioCobranca.getTime() - inicioHoje.getTime();
    const dias = Math.ceil(ms / (1000 * 60 * 60 * 24));
    return Math.max(0, dias);
  }

  private normalizarData(valor: string): Date | null {
    const texto = String(valor ?? '').trim();
    if (!texto) return null;

    const apenasData = texto.split(' ')[0].split('T')[0];
    const [y, m, d] = apenasData.split('-').map((n) => Number(n));
    if (!y || !m || !d) return null;

    return new Date(y, m - 1, d);
  }

  private labelMetodoPagamento(metodo: 'cartao' | 'boleto' | 'pix'): string {
    if (metodo === 'pix') return 'Pix';
    if (metodo === 'boleto') return 'boleto';
    return 'cartão';
  }

  /** Ignora `mudanca_ativa` vazia/`{}` da API — só retém mudança real em andamento. */
  private normalizarMudancaAtiva(
    mudanca: Record<string, unknown> | null,
  ): typeof this.mudancaAtiva {
    if (!mudanca || typeof mudanca !== 'object') {
      return null;
    }

    const id = Number(mudanca['id'] ?? 0);
    const tipo = String(mudanca['tipo_mudanca'] ?? '').trim();
    const status = String(mudanca['status'] ?? '').trim().toLowerCase();
    const tiposValidos = new Set([
      'upgrade',
      'downgrade',
      'troca_ciclo',
      'cancelamento',
      'reativacao',
    ]);
    const statusEmAndamento = new Set(['agendada', 'pendente_pagamento', 'paga']);

    if (!(id > 0) || !tiposValidos.has(tipo) || !statusEmAndamento.has(status)) {
      return null;
    }

    return {
      id,
      tipo_mudanca: tipo as
        | 'upgrade'
        | 'downgrade'
        | 'troca_ciclo'
        | 'cancelamento'
        | 'reativacao',
      status: String(mudanca['status'] ?? ''),
      valor_diferenca: String(mudanca['valor_diferenca'] ?? '0.00'),
      metodo_pagamento: String(mudanca['metodo_pagamento'] ?? ''),
      efetivar_em: String(mudanca['efetivar_em'] ?? ''),
    };
  }

  private async carregarDadosTrocaPlano(): Promise<void> {
    try {
      const r = await this.api.get('assinaturasplano');
      const data = (r.data as Record<string, unknown>) ?? {};
      const possuiAssinatura = Boolean(data['possui_assinatura']);
      if (!possuiAssinatura) {
        this.planosDisponiveis = [];
        this.mudancaAtiva = null;
        this.trocaDisponivel = false;
        this.mensagemTroca = '';
        return;
      }

      this.trocaDisponivel = Boolean(data['troca_disponivel']);
      this.mensagemTroca = String(data['mensagem_troca'] ?? '');

      const planos = (data['planos_disponiveis'] as Array<Record<string, unknown>>) ?? [];
      this.planosDisponiveis = planos.map((plano) => ({
        id: Number(plano['id'] ?? 0),
        titulo: String(plano['titulo'] ?? ''),
        valor: String(plano['valor'] ?? '0.00'),
        tipo: String(plano['tipo'] ?? ''),
        periodicidade: String(plano['periodicidade'] ?? 'mensal'),
        descricao: String(plano['descricao'] ?? ''),
        codigo: String(plano['codigo'] ?? ''),
        tipo_mudanca: String(plano['tipo_mudanca'] ?? ''),
        label_quando: String(plano['label_quando'] ?? ''),
        valor_diferenca: String(plano['valor_diferenca'] ?? '0.00'),
        efetivar_em: String(plano['efetivar_em'] ?? ''),
        mensagem: String(plano['mensagem'] ?? ''),
      }));

      const mudanca = (data['mudanca_ativa'] as Record<string, unknown>) ?? null;
      this.mudancaAtiva = this.normalizarMudancaAtiva(mudanca);
    } catch {
      this.planosDisponiveis = [];
      this.mudancaAtiva = null;
    }
  }
}
